import { FileText, X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Attachment, AttachmentType, AttachmentStatus } from "@/types/attachments";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

interface AttachmentChipProps {
  attachment: Attachment;
  onRemove: (id: string) => void;
}

const AttachmentChip = ({ attachment, onRemove }: AttachmentChipProps) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const getTypeLabel = () => {
    switch (attachment.type) {
      case AttachmentType.PDF:
        return "PDF";
      case AttachmentType.IMAGE:
        return "Image";
      case AttachmentType.YOUTUBE:
        return "Video";
      default:
        return "";
    }
  };

  const getStatusLabel = () => {
    switch (attachment.status) {
      case AttachmentStatus.PENDING:
        return "Preparing...";
      case AttachmentStatus.UPLOADING:
        return "Uploading...";
      case AttachmentStatus.PROCESSING:
        return attachment.type === AttachmentType.IMAGE ? "Converting to PDF..." : "Processing...";
      case AttachmentStatus.COMPLETE:
        return "Ready";
      case AttachmentStatus.ERROR:
        return attachment.error || "Failed";
      default:
        return getTypeLabel();
    }
  };

  const getStatusIcon = () => {
    switch (attachment.status) {
      case AttachmentStatus.PENDING:
      case AttachmentStatus.UPLOADING:
      case AttachmentStatus.PROCESSING:
        return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      case AttachmentStatus.COMPLETE:
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case AttachmentStatus.ERROR:
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const isProcessing = attachment.status === AttachmentStatus.PENDING || 
                       attachment.status === AttachmentStatus.UPLOADING || 
                       attachment.status === AttachmentStatus.PROCESSING;

  return (
    <div 
      className="bg-white border-[0.75px] border-[#d7d7d7] rounded-[9px] flex items-center justify-between"
      style={{
        width: '154.5px',
        paddingLeft: '6px',
        paddingRight: '9px',
        paddingTop: '6px',
        paddingBottom: '6px'
      }}
    >
      {/* Left Section - Icon + Text */}
      <div className="flex items-center" style={{ gap: '7.5px' }}>
        {/* Icon Container */}
        <div 
          className="bg-[#ededed] rounded-[6.75px] flex items-center justify-center relative flex-shrink-0"
          style={{ width: '25.5px', height: '26.25px' }}
        >
          {attachment.thumbnail && attachment.type === AttachmentType.IMAGE ? (
            <img 
              src={attachment.thumbnail}
              alt={attachment.name}
              className="absolute rounded-[6.75px] object-cover"
              style={{
                left: '-0.75px',
                top: '-11.25px',
                width: '26.94px',
                height: '44.25px'
              }}
            />
          ) : (
            <FileText className="text-[#7a7a7a]" style={{ width: '15px', height: '15px', strokeWidth: 1.25 }} />
          )}
          
          {/* Loading overlay */}
          {isProcessing && (
            <div className="absolute inset-0 flex items-center justify-center bg-[#ededed] bg-opacity-80 rounded-[6.75px]">
              <Loader2 className="w-3 h-3 text-blue-500 animate-spin" />
            </div>
          )}
        </div>
        
        {/* Text Column */}
        <div className="flex flex-col overflow-hidden" style={{ gap: '7.5px' }}>
          <span 
            className="truncate leading-none"
            style={{
              fontFamily: 'Lexend Deca',
              fontSize: '9px',
              lineHeight: '9.75px',
              color: '#000000'
            }}
          >
            {attachment.name.length > 15 ? `${attachment.name.substring(0, 15)}...` : attachment.name}
          </span>
          <span 
            style={{
              fontFamily: 'Lexend Deca',
              fontSize: '8.25px',
              lineHeight: '6px',
              color: 'rgba(0,0,0,0.5)'
            }}
          >
            {getTypeLabel()}
          </span>
          
          {/* Status indicator for errors */}
          {attachment.status === AttachmentStatus.ERROR && (
            <span 
              className="text-red-600"
              style={{
                fontFamily: 'Lexend Deca',
                fontSize: '7px',
                lineHeight: '6px'
              }}
            >
              Failed
            </span>
          )}
        </div>
      </div>

      {/* Right Section - Close Icon */}
      <Popover open={showConfirmation} onOpenChange={setShowConfirmation}>
        <PopoverTrigger asChild>
          <button
            disabled={isProcessing}
            className="flex-shrink-0 transition-opacity"
            style={{
              opacity: isProcessing ? 0.3 : 0.5,
              cursor: isProcessing ? 'not-allowed' : 'pointer'
            }}
            title={isProcessing ? "Cannot remove while processing" : "Remove attachment"}
          >
            <X className="text-black" style={{ width: '12px', height: '12px' }} />
          </button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto p-3 bg-gray-100 border border-gray-200 shadow-md"
          side="top"
          align="end"
        >
          <div className="flex flex-col gap-2">
            <p className="text-sm text-foreground font-medium">
              Are you sure?
            </p>
            <Button
              onClick={() => {
                onRemove(attachment.id);
                setShowConfirmation(false);
              }}
              className="bg-red-500 hover:bg-red-600 text-white text-sm py-1 px-4 h-auto"
            >
              Delete
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default AttachmentChip;
